<template>
  <div>
    <attach-document
      v-if="currentTemplate"
      :template_id="currentTemplate.id"
      :visible.sync="visibleDocumentModal"
      :associate_id="associate.id"
      @save="reloadAssociate(true)"
    />

    <create-request
      v-if="documentForRequest"
      :visible.sync="visibleRequestModal"
      :target-object="documentForRequest.document"
      :type="documentForRequest.type"
      :specific-associates="[associate.id]"
      @add="reloadAssociate(true)"
    />

    <div class="required-document-info flex flex-column gap-3" :class="{ 'required-document-info--modal': mode === 'modal' }">
      <div class="required-document-header">
        <h3 class="m-0">Documentation Compliance</h3>
        <a-button :loading="loadingReport" type="primary" icon="export" @click="handleDownloadReport"
          >Download</a-button
        >
      </div>
      <div :style="{ height: '680px' }" class="flex align-items-center justify-content-center" v-if="loading">
        <a-spin />
      </div>
      <template v-else>
        <div class="flex flex-column gap-2 pr-2 required-document-wrapper">
          <dynamic-form v-if="!isStaff" />

          <div class="grid required-document-row" v-for="item in complianceItems" :key="getItemKey(item)">
            <template v-if="item.type === 'attached'">
              <div class="flex flex-column col-8">
                <span> {{ item.template.name }} </span
                ><span class="required-document-source">Source: {{ item.source }}</span
                ><span
                  :style="{
                    color: getAttachedDocumentStatus(item.document).color
                  }"
                  >{{ getAttachedDocumentStatus(item.document).title }}</span
                >
              </div>
              <div class="col-4 flex gap-1 justify-content-end">
                <a-button
                  size="small"
                  title="Create Request"
                  @click.stop="handleSendRequest(item.template, 'associate_document')"
                >
                  Create Request
                </a-button>
                <a-button
                  size="small"
                  :title="!item.document ? 'Add Document' : 'View Document'"
                  @click="!item.document ? openDocumentModal(item.template) : handleAttachedView(item.document)"
                >
                  {{ !item.document ? 'Add Document' : 'View Document' }}
                </a-button>
              </div>
            </template>

            <template v-else-if="item.type === 'company'">
              <div class="flex flex-column col-8">
                <span> {{ item.project_document.name }} </span
                ><span class="required-document-source">Source: {{ item.source }}</span
                ><span :style="{ color: getDocumentStatus(item).color }">{{ getDocumentStatus(item).title }}</span>
              </div>
              <div class="col-4 flex gap-1 justify-content-end">
                <a-button
                  v-if="!item.project_document.awaiting_signature && !item?.signed_document"
                  size="small"
                  title="Send for Signature"
                  @click.stop="handleSendRequest(item.project_document, 'document')"
                >
                  Send for Signature
                </a-button>
                <a-button
                  size="small"
                  title="Backfill"
                  :loading="loadingBackfill[item.project_document.id]"
                  @click="handleBackfill(item.project_document)"
                >
                  Backfill
                </a-button>
                <a-button
                  v-if="item?.signed_document"
                  size="small"
                  title="View"
                  @click="handleDocumentView(item.signed_document)"
                >
                  View
                </a-button></div
            ></template>

            <template v-else>
              <div class="flex flex-column col-8">
                <span> {{ item.title }} </span
                ><span class="required-document-source">Source: {{ item.source }}</span
                ><span
                  :style="{
                    color: item.status === 'Actual' ? '#10b981' : '#ef4444'
                  }"
                  >{{ item.status }}</span
                >
              </div>
              <div class="col-4 flex gap-1 justify-content-end">
                <a-button size="small" title="View Legal Entity" @click="$emit('open')">View Legal Entity</a-button></div
            ></template>
          </div></div
      ></template>
    </div>
  </div>
</template>

<script>
import { Button, notification, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ProfileCreateDocumentTemplate from '@/modules/profile/components/profile-create-document-template.vue'
import CompleteRequestCreate from '@/modules/complete-request/components/complete-request-create.vue'
import RequiredDocumentsDynamicForm from './required-documents-dynamic-form.vue'
import util from '@/util'

const { mapActions: modalActions } = createNamespacedHelpers('modals')
const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const {
  mapActions: documentActions,
  mapState: documentState,
  mapGetters: documentGetters
} = createNamespacedHelpers('document')

export default {
  components: {
    'a-button': Button,
    'a-spin': Spin,
    'create-request': CompleteRequestCreate,
    'attach-document': ProfileCreateDocumentTemplate,
    'dynamic-form': RequiredDocumentsDynamicForm
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'panel'
    }
  },
  data() {
    return {
      visibleDocumentModal: false,
      visibleRequestModal: false,

      currentTemplate: undefined,
      documentForRequest: undefined,

      loadingBackfill: {},
      loadingReport: false,

      activeKey: ['attached', 'company', 'legal_entity']
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate,
      compliance: state => state.associateCompliance,
      certification: state => state.associateCertification
    }),
    ...documentState({
      associateDocuments: state => state.associateDocuments
    }),
    ...documentGetters(['getAssociateDocumentStatus', 'getAssociateDocumentOrigin']),

    isStaff() {
      return Boolean(this.$store.state.applicationState.currentProject?.staffing)
    },

    requiredDocs() {
      const allDocuments = [...this.requiredAttachedDocs, ...this.requiredCompanyDocs, ...this.requiredLegalEntityDocs]

      return allDocuments.sort((a, b) => {
        const getPriority = doc => {
          if (doc.type === 'attached') {
            if (!doc.document) return 0
            if (doc.document.status === 'expired') return 1
            return 2
          }

          if (doc.type === 'company') {
            if (!doc.signed_document) return 0
            if (doc.awaiting_signature) return 1
            return 2
          }

          if (doc.type === 'legal_entity') {
            if (doc.status === 'Missing') return 0
            return 2
          }

          return 3
        }

        const priorityA = getPriority(a)
        const priorityB = getPriority(b)

        if (priorityA !== priorityB) {
          return priorityA - priorityB
        }

        return 0
      })
    },

    certificationDocs() {
      const allDocuments = [
        ...(this.certification?.required_associate_docs ?? []).map(item => ({
          ...item,
          type: 'attached'
        })),
        ...(this.certification?.required_project_docs ?? []).map(item => ({
          ...item,
          type: 'company'
        }))
      ]

      return allDocuments.sort((a, b) => {
        const getPriority = doc => {
          if (doc.type === 'attached') {
            if (!doc.document) return 0
            if (doc.document.status === 'expired') return 1
            return 2
          }

          if (doc.type === 'company') {
            if (!doc.signed_document) return 0
            if (doc.awaiting_signature) return 1
            return 2
          }

          return 3
        }

        const priorityA = getPriority(a)
        const priorityB = getPriority(b)

        if (priorityA !== priorityB) {
          return priorityA - priorityB
        }

        return 0
      })
    },

    complianceItems() {
      const certificationItems = this.certificationDocs.map(item => ({
        ...item,
        source: 'ePeople Certification'
      }))
      const requiredItems = this.requiredDocs.map(item => ({
        ...item,
        source: item.type === 'legal_entity' ? 'Legal Entity' : 'Compliance'
      }))

      return [...certificationItems, ...requiredItems].sort((a, b) => {
        const priorityA = this.getCompliancePriority(a)
        const priorityB = this.getCompliancePriority(b)

        if (priorityA !== priorityB) {
          return priorityA - priorityB
        }

        return this.getComplianceName(a).localeCompare(this.getComplianceName(b))
      })
    },

    requiredAttachedDocs() {
      const complianceDocs = this.compliance?.required_associate_docs ?? []

      const certDocTemplateIds = new Set((this.certification?.required_associate_docs ?? []).map(el => el.template.id))

      const docs = complianceDocs
        .filter(item => !certDocTemplateIds.has(item.template.id))
        .map(item => ({
          ...item,
          type: 'attached'
        }))

      return docs
    },

    requiredCompanyDocs() {
      const complianceDocs = this.compliance?.required_project_docs ?? []

      const certDocIds = new Set((this.certification?.required_project_docs ?? []).map(el => el.project_document.id))

      const docs = complianceDocs
        .filter(item => !certDocIds.has(item.project_document.id))
        .map(item => ({
          ...item,
          type: 'company'
        }))

      return docs
    },

    requiredLegalEntityDocs() {
      const legal_entity = this.associate?.payment_info?.legal_entity

      if (!legal_entity) return []
      const hasFiles = files => Array.isArray(files) && files.length > 0

      const agency_documents = [
        {
          title: 'Tax ID Letter from the IRS',
          status: hasFiles(legal_entity.tax_id_files) ? 'Actual' : 'Missing',
          type: 'legal_entity'
        },
        {
          title: 'Articles of Organization',
          status: hasFiles(legal_entity.article_files) ? 'Actual' : 'Missing',
          type: 'legal_entity'
        }
      ]

      const allDocuments = [
        ...(legal_entity.type === 'agency' || legal_entity.type === 'marketplace' ? [...agency_documents] : []),
        {
          title: 'Contract',
          status: hasFiles(legal_entity.contract_files) ? 'Actual' : 'Missing',
          type: 'legal_entity'
        },
        {
          title: 'W9 Form',
          status: hasFiles(legal_entity.w9_files) ? 'Actual' : 'Missing',
          type: 'legal_entity'
        }
      ]

      return allDocuments
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...profileActions([
      'reloadAssociate',
      'setAssociateCompliance',
      'downloadComplianceReport',
      'setAssociateCertification'
    ]),
    ...documentActions(['setAssociateDocuments']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    getItemKey(item) {
      switch (item.type) {
        case 'attached':
          return `${item.source || 'document'}-attached-${item.template.id}`
        case 'company':
          return `${item.source || 'document'}-company-${item.project_document.id}`
        case 'legal_entity':
        default:
          return item.title
      }
    },

    getComplianceName(item) {
      if (item.type === 'attached') {
        return item.template?.name || ''
      }

      if (item.type === 'company') {
        return item.project_document?.name || ''
      }

      return item.title || ''
    },

    getCompliancePriority(item) {
      if (item.type === 'attached') {
        if (!item.document) return 0
        if (item.document.status === 'unverified') return 1
        if (item.document.status === 'expired') return 2
        return 3
      }

      if (item.type === 'company') {
        if (!item.signed_document) return 0
        if (item.awaiting_signature) return 2
        return 3
      }

      if (item.type === 'legal_entity') {
        return item.status === 'Actual' ? 3 : 0
      }

      return 4
    },

    getAttachedDocumentStatus(document) {
      if (!document) {
        return {
          title: 'Missing',
          color: '#ef4444'
        }
      } else {
        return {
          title: document.status === 'accepted' ? 'Actual' : this.getAssociateDocumentStatus(document.status).title,
          color: this.getAssociateDocumentStatus(document.status).color
        }
      }
    },

    getDocumentStatus(document) {
      if (!document.signed_document) {
        return {
          title: 'Missing',
          color: '#ef4444'
        }
      } else if (document?.awaiting_signature) {
        return {
          title: 'Unsigned',
          color: '#f59e0b'
        }
      } else {
        return {
          title: 'Actual',
          color: '#10b981'
        }
      }
    },

    needAttach(document) {
      return this.getAttachedDocumentStatus(document).title !== 'Actual'
    },

    openDocumentModal(template) {
      this.currentTemplate = template
      this.visibleDocumentModal = true
    },

    handleSendRequest(document, type) {
      this.documentForRequest = {
        document,
        type
      }
      this.visibleRequestModal = true
    },

    handleDocumentView(document) {
      this.openModal({
        type: 'documentView',
        data: {
          visible: true,
          associate_id: this.associate.id,
          version: document.current_version,
          document_id: document.id
        }
      })
    },

    handleBackfill(document) {
      this.openModal({
        type: 'backfill',
        data: {
          visible: true,
          targetObject: document,
          type: 'project_document',
          specificAssociates: [this.associate.id],
          add: () => this.reloadAssociate(true)
        }
      })
    },

    async handleDownloadReport() {
      this.loadingReport = true

      try {
        const { file_url } = await this.downloadComplianceReport({
          associate_id: this.associate.id
        })
        util.downloadFile(file_url)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingReport = false
      }
    },

    handleAttachedView(initial_document) {
      const updateDocumentInStore = (updatedDocument, isRenew = false) => {
        const documentId = isRenew ? updatedDocument.parent_id : updatedDocument.id
        const templateId = updatedDocument.template_id
        const associateDocumentItems = this.associateDocuments?.items ?? []

        const updatedDocuments = {
          ...this.associateDocuments,
          items: isRenew
            ? associateDocumentItems.filter(item => item.id !== documentId)
            : associateDocumentItems.map(doc => (doc.id === documentId ? updatedDocument : doc))
        }

        const updateRequiredDocs = requiredDocs => {
          return (requiredDocs ?? []).map(item => {
            if (item?.template?.id === templateId) {
              return { ...item, document: { ...updatedDocument } }
            } else if (item?.document?.id === documentId && item?.template?.id !== templateId) {
              return { ...item, document: null }
            }
            return item
          })
        }

        const updatedCompliance = {
          ...this.compliance,
          required_associate_docs: updateRequiredDocs(this.compliance?.required_associate_docs)
        }

        const updatedCertification = {
          ...this.certification,
          required_associate_docs: updateRequiredDocs(this.certification?.required_associate_docs)
        }

        this.setAssociateCompliance(updatedCompliance)
        this.setAssociateCertification(updatedCertification)
        this.setAssociateDocuments(updatedDocuments)
      }

      this.openModal({
        type: 'attachedView',
        data: {
          visible: true,
          initial_document: { ...initial_document, associate: this.associate },
          onRenew: updatedDocument => updateDocumentInStore(updatedDocument, true),
          onUpdate: updatedDocument => updateDocumentInStore(updatedDocument, false)
        }
      })
    }
  }
}
</script>

<style scoped>
.required-document-info {
  position: absolute;
  right: -330px;
  background-color: var(--bg-component);
  width: 310px;
  height: 100%;
  top: 0;
  border-radius: 12px;
  padding: 16px 20px;
}

.required-document-info--modal {
  position: static;
  width: 100%;
  padding: 0;
  border-radius: 0;
}

.required-document-info--modal .required-document-header {
  flex-direction: row;
  justify-content: space-between;
}

.required-document-info--modal .required-document-wrapper {
  max-height: 540px;
}

.required-document-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.required-document-wrapper {
  max-height: 100%;
  overflow-y: auto;
}

.required-document-row {
  align-items: center;
  border-bottom: 1px solid var(--border-color-base);
  padding: 8px 0;
}

.required-document-source {
  color: var(--text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}

::v-deep .ant-collapse-borderless {
  background-color: transparent;
}

::v-deep .ant-collapse-borderless > .ant-collapse-item {
  border: none;
}

::v-deep .ant-collapse-icon-position-right > .ant-collapse-item > .ant-collapse-header {
  padding: 0;
  font-weight: 700;
  margin-bottom: 12px;
}

::v-deep .ant-collapse-borderless > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
  padding: 0;
}

::v-deep .ant-collapse-icon-position-right > .ant-collapse-item > .ant-collapse-header .ant-collapse-arrow {
  right: 0;
}

@media screen and (max-width: 1920px) {
  .required-document-info {
    position: static;
    width: 100%;
    padding: 0;
  }

  .required-document-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 16px;
  }

  .required-document-wrapper {
    max-height: 300px;
    overflow-y: auto;
  }
}
</style>
